import { useMemo } from 'react';
import { fetchOpenFormContactDetails } from '../../../common/fetch.js';
import {
  confirmMsg,
  formHasPassiveContactMsg,
  previousMsg,
  submittingFormMsg,
  summaryMsg,
  t,
} from '../../../common/i18n/index.js';
import { type OpenFormAnswers } from '../OpenFormAnswers.js';
import { OpenFormButton } from '../OpenFormComponents/OpenFormButton.js';
import { OpenFormGridCol, OpenFormGridRow } from '../OpenFormComponents/OpenFormGrid.js';
import { OpenFormIcon } from '../OpenFormComponents/OpenFormIcon.js';
import { OpenFormNotice } from '../OpenFormComponents/OpenFormNotifications';
import { type OpenForm } from '../OpenFormHooks/useOpenForm.js';
import { type OpenFormGoto } from '../OpenFormHooks/useOpenFormGoto.js';
import { useOpenFormSummary } from '../OpenFormHooks/useOpenFormSummary.js';
import { useOpenFormAsync, useOpenFormState } from '../OpenFormProvider.js';
import { OpenFormSummaryBillingAccount } from './OpenFormSummary/OpenFormSummaryBillingAccount.js';
import { OpenFormSummaryDeliveryDateTime } from './OpenFormSummary/OpenFormSummaryDeliveryDateTime.js';
import { OpenFormSummaryOrderingContact } from './OpenFormSummary/OpenFormSummaryOrderingContact.js';
import { OpenFormSummaryOtherContacts } from './OpenFormSummary/OpenFormSummaryOtherContacts.js';
import { OpenFormSummaryProductTable } from './OpenFormSummary/OpenFormSummaryProductTable.js';
import { OpenFormSummarySubscriptionReferences } from './OpenFormSummary/OpenFormSummarySubscriptionReferences.js';
import { OpenFormZeroPrice } from './OpenFormSummary/OpenFormZeroPrice.js';

export const OpenFormViewSummary = ({
  answers,
  disabled,
  form,
  goto,
  preview,
}: {
  answers: OpenFormAnswers;
  disabled: boolean;
  form: OpenForm;
  goto: OpenFormGoto;
  preview: boolean;
}) => {
  const async = useOpenFormAsync();
  const summary = useOpenFormSummary(form, answers);
  const openFormState = useOpenFormState();

  const dataMismatch = useMemo(() => {
    if (!summary) {
      return false;
    }

    const apiProducts = [...summary.products, ...summary.services].map(item => item.choice);
    const stateProducts = Array.from(openFormState.answers.values()).flatMap(({ charges = {} }) =>
      Object.entries(charges)
        .filter(([, charge]) => charge?.linkedValue)
        .map(([choiceId]) => choiceId)
    );

    if (apiProducts.length !== stateProducts.length) {
      return true;
    }

    const apiSet = new Set(apiProducts);
    const stateSet = new Set(stateProducts);

    return !apiProducts.every(item => stateSet.has(item)) || !stateProducts.every(item => apiSet.has(item));
  }, [summary, openFormState.answers]);

  const formComplete = form.sections.every(answers.isComplete);
  const zeroMismatch = summary?.zeroPriceMismatch ?? false;
  const zeroValid = summary?.zeroPriceValid ?? true;
  const canSubmit = zeroMismatch ? zeroValid : formComplete;
  const isDisabled = disabled || !canSubmit || dataMismatch;

  const validateContacts = async (): Promise<boolean> => {
    const contactIds = Array.from(openFormState.answers.values())
      .map(answerValue => answerValue.contact?.Id)
      .filter(Boolean)
      .join(',');

    const response = await fetchOpenFormContactDetails({ contactIds });
    const hasPassiveContacts = response.contactDetails?.some(contact => !contact.isActive) ?? false;

    if (hasPassiveContacts) {
      async.actions.notification({ text: t.LTQW(formHasPassiveContactMsg), type: 'warning' });
    }

    return !hasPassiveContacts;
  };

  const handleSubmit = async () => {
    const isValid = await validateContacts();
    if (!isValid) {
      return;
    }

    try {
      async.fetch
        .postOpenForm(summary!.openFormCustomerNeedRequest)
        .resolved(() => goto({ page: 'completed' }))
        .rejected(text => async.actions.notification({ text, type: 'warning' }))
        .execute({ disable: true, message: t.JNCH(submittingFormMsg) });
    } catch (error) {
      async.actions.notification({ text: error, type: 'warning' });
    }
  };

  return !summary ? null : (
    <div className="of-openform__view__summary">
      <OpenFormGridRow>
        <h3>{t.QF5T(summaryMsg)}</h3>
      </OpenFormGridRow>
      <OpenFormSummaryOrderingContact summary={summary} />
      <OpenFormSummaryBillingAccount summary={summary} />
      <OpenFormSummarySubscriptionReferences summary={summary} />
      <OpenFormSummaryOtherContacts summary={summary} />
      <OpenFormSummaryDeliveryDateTime summary={summary} />
      <OpenFormSummaryProductTable answers={answers} disabled={disabled} summary={summary} />
      <OpenFormZeroPrice disabled={disabled} zeroPriceMismatch={zeroMismatch} />
      <OpenFormGridRow className="of-openform__view__footer">
        <OpenFormGridCol className="of-openform__view__footer__content">
          <OpenFormButton
            iconPosition="left"
            icon={<OpenFormIcon icon="arrow-left" />}
            text={t.CACI(previousMsg)}
            color="link"
            size="m"
            disabled={disabled}
            onClick={() => goto({ page: 'prev' })}
          />
          {preview ? null : (
            <OpenFormButton
              iconPosition="right"
              icon={<OpenFormIcon icon="check" />}
              text={t.QVYK(confirmMsg)}
              color="primary"
              size="m"
              disabled={isDisabled}
              onClick={handleSubmit}
            />
          )}
          {dataMismatch && (
            <OpenFormNotice
              text={t.ES7M(
                'There is an error or missing product data in the product data used by the form. Please contact the Salesforce back office with a support request to fix the issue.'
              )}
              type="error"
              hide={() => {
                delete self.history.state.retry;
                self.history.replaceState(self.history.state, '', self.location.href);
                self.location.reload();
              }}
            />
          )}
        </OpenFormGridCol>
      </OpenFormGridRow>
    </div>
  );
};
